import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse } from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || '/api',
      timeout: 60000, // 增加超时时间到60秒
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 请求拦截器
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        const url = (config.baseURL || '') + (config.url || '');
        
        // 只对需要认证的API添加token检查日志
        const isAuthRequired = url.includes('/auth/') || 
                              url.includes('/upload/') || 
                              url.includes('/admin/') ||
                              url.includes('/team-activities/user') ||
                              url.includes('/team-activities/admin');
        
        if (isAuthRequired) {
          console.log('🔑 当前token:', token ? '存在' : '不存在');
          console.log('🌐 请求URL:', url);
        }
        
        if (token && token.trim() !== '') {
          config.headers.Authorization = `Bearer ${token}`;
          if (isAuthRequired) {
            console.log('✅ 已添加认证头');
          }
        } else if (isAuthRequired) {
          console.log('⚠️ 未找到有效token，请求可能被拒绝');
        }
        return config;
      },
      (error) => {
        console.error('❌ 请求拦截器错误:', error);
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // 通用GET请求
  async get<T>(url: string, params?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.get(url, { params });
      return response.data;
    } catch (error: any) {
      console.error(`API请求失败: ${url}`, error);
      if (error.code === 'ECONNABORTED') {
        console.error('请求超时，请检查网络连接');
      }
      throw error;
    }
  }

  // 通用POST请求  
  async post(url: string, data?: any): Promise<any> {
    const response = await this.api.post(url, data);
    return response.data;
  }

  // 通用PUT请求
  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.put(url, data);
    return response.data;
  }

  // 通用DELETE请求
  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.api.delete(url);
    return response.data;
  }

  // 通用PATCH请求
  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.patch(url, data);
    return response.data;
  }

  // 文件上传
  async upload<T>(url: string, formData: FormData, options?: any): Promise<ApiResponse<T>> {
    const response = await this.api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...options,
    });
    return response.data;
  }

  // 发送邮箱验证码
  async sendEmailVerificationCode(email: string): Promise<ApiResponse<any>> {
    // 使用独立的邮箱验证服务
    const response = await fetch('https://ynuzhangwenhua.cn/api/auth/send-verification-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email })
    });
    return response.json();
  }

  // 验证邮箱验证码
  async verifyEmailCode(email: string, code: string): Promise<ApiResponse<any>> {
    // 使用独立的邮箱验证服务
    const response = await fetch('https://ynuzhangwenhua.cn/api/auth/verify-email-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code })
    });
    return response.json();
  }
}

export const apiService = new ApiService();
export default apiService; 